/**
 * Tipos compartidos con el backend (Cloud Functions)
 * Mantener sincronizado con functions/src/types/index.ts
 */

import { Timestamp } from 'firebase/firestore';

// ========================================
// User Types
// ========================================

export type UserRole = 'super_admin' | 'franchise_owner' | 'admin' | 'barber' | 'client' | 'guest';

export interface User {
  id: string;
  email: string | null;
  displayName: string;
  photoURL: string | null;
  phoneNumber: string | null;
  role: UserRole;
  isAnonymous: boolean;
  isVIP?: boolean; // VIP users have access to appointments
  franchiseId?: string;
  branchId?: string;
  queuePoints: number;
  queueHistory: {
    totalCompleted: number;
    totalNoShows: number;
    totalExpired: number;
    totalCancelled: number;
  };
  // Stamp card for loyalty program
  stampCard?: {
    stamps: number;              // Current stamps (0-10)
    totalCompleted: number;      // Total cards completed
    lastStampAt?: Timestamp;     // Last time stamp was added
  };
  // Notification preferences
  enablePushNotifications?: boolean;
  enableEmailNotifications?: boolean;
  notifyOnPositionUpdate?: boolean;
  notifyOnYourTurn?: boolean;
  notifyOnCancellation?: boolean;

  // Post notification preferences (Phase 3)
  postNotificationPreferences?: PostNotificationPreferences;

  // Account deletion (soft delete)
  isDeleted?: boolean;           // True if account is cancelled/deleted
  deletedAt?: Timestamp;         // When the account was deleted
  deletionReason?: string;       // Optional reason for deletion

  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface CustomClaims {
  role: UserRole;
  isAnonymous: boolean;
  isVIP?: boolean;
  franchiseId?: string;
  branchId?: string;
}

// ========================================
// Franchise Types
// ========================================

export interface Franchise {
  franchiseId: string;
  name: string;
  ownerUserId: string;
  logo?: string;
  description?: string;
  website?: string;
  email: string;
  phone: string;
  stripeAccountId?: string;
  stripeSubscriptionId?: string;
  planTier: 'free' | 'basic' | 'premium' | 'enterprise';
  planExpiresAt?: Timestamp;
  isActive: boolean;

  // Commission configuration
  defaultCommissionRate?: number; // Default commission percentage (0-100) for barbers

  // Landing page configuration
  schedule?: {
    weekdays: string; // e.g., "10:00 - 21:00"
    saturday: string; // e.g., "10:00 - 20:00"
    sunday: string;   // e.g., "Cerrado"
  };
  averageWaitTime?: string; // e.g., "15min"

  // Google Reviews
  googlePlaceId?: string;

  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface Branch {
  branchId: string;
  franchiseId: string;
  name: string;
  address: string;
  city: string;
  postalCode: string;
  province: string;
  country: string;
  phone: string;
  email?: string;
  photo?: string;
  location?: {
    latitude: number;
    longitude: number;
  };
  schedule: BranchSchedule;
  isActive: boolean;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface BranchSchedule {
  monday: DaySchedule;
  tuesday: DaySchedule;
  wednesday: DaySchedule;
  thursday: DaySchedule;
  friday: DaySchedule;
  saturday: DaySchedule;
  sunday: DaySchedule;
}

export interface DaySchedule {
  isOpen: boolean;
  open?: string; // "09:00"
  close?: string; // "21:00"
  breakStart?: string; // "14:00"
  breakEnd?: string; // "16:00"
}

// ========================================
// Barber Types
// ========================================

export interface Barber {
  barberId: string;
  userId: string;
  email: string;
  franchiseId: string;
  branchId: string;
  displayName: string;
  photoURL?: string;
  specialties: string[];
  bio?: string;
  schedule: BarberSchedule;
  isActive: boolean;
  isAvailable: boolean;

  // Commission configuration (override franchise default)
  commissionRate?: number; // Individual commission percentage (0-100), overrides franchise default

  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface BarberSchedule {
  monday: BarberDaySchedule;
  tuesday: BarberDaySchedule;
  wednesday: BarberDaySchedule;
  thursday: BarberDaySchedule;
  friday: BarberDaySchedule;
  saturday: BarberDaySchedule;
  sunday: BarberDaySchedule;
}

export interface BarberDaySchedule {
  isWorking: boolean;
  workStart?: string; // "09:00"
  workEnd?: string; // "21:00"
  breakStart?: string; // "14:00"
  breakEnd?: string; // "16:00"
}

/**
 * Barber availability status types
 */
export type BarberStatusType = 'available' | 'busy' | 'on_break' | 'offline';

/**
 * Break reason types for barbers
 * - eating: Pausa para comer (temporal, permite turnos después)
 * - quick_errand: Gestión rápida (temporal, vuelvo en X minutos)
 * - personal: Asuntos personales (temporal, permite turnos después)
 * - day_off: Hoy libro (no disponible para el resto del día)
 * - other: Otro motivo
 */
export type BreakReason = 'eating' | 'quick_errand' | 'personal' | 'day_off' | 'other';

/**
 * Break reason display names
 */
export const BREAK_REASON_LABELS: Record<BreakReason, string> = {
  eating: 'Comiendo',
  quick_errand: 'Gestión rápida',
  personal: 'Asuntos personales',
  day_off: 'Hoy libro',
  other: 'Otro',
};

/**
 * Real-time barber status tracking
 * Tracks barber availability, current service, and break information
 */
export interface BarberStatus {
  barberId: string;
  franchiseId: string;
  branchId: string;

  // Current status
  status: BarberStatusType;

  // Current service tracking
  currentTicketId?: string; // Queue ticket being served
  // No se guarda aquí el nombre del cliente: desde #19 la colección es de
  // lectura pública para la pantalla de la sala, que no tiene sesión.
  serviceStartedAt?: Timestamp; // When current service started

  // Break tracking
  breakStartedAt?: Timestamp;
  breakExpectedEndAt?: Timestamp; // Expected return time
  breakReason?: BreakReason; // Reason for break
  breakDurationMinutes?: number; // Expected duration in minutes
  /** Cuándo empieza realmente la pausa: al acabar la cola que el profesional
   *  tiene delante, no cuando la marca (incidencia #22). */
  breakStartAt?: Timestamp;
  breakIsTemporary?: boolean; // If true, clients can book after return time

  // Schedule tracking
  shiftStartTime?: string; // e.g., "09:00"
  shiftEndTime?: string; // e.g., "18:00"

  // Timestamps
  lastStatusChange: Timestamp;
  lastActiveAt: Timestamp; // Last activity timestamp

  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// ========================================
// Service Types
// ========================================

/**
 * Service categories for barbershop services
 */
export type ServiceCategory = 'haircut' | 'beard' | 'combo' | 'color' | 'styling' | 'treatment' | 'other';

/**
 * Barbershop service definition
 * Includes pricing, duration, and categorization
 */
export interface Service {
  serviceId: string;
  franchiseId: string;
  branchId?: string; // Sucursal específica (opcional para servicios de franquicia)
  name: string;
  description?: string;
  duration: number; // Duration in minutes
  price: number; // Price in cents (EUR)
  currency?: string; // Currency code (default: EUR)
  category: ServiceCategory;
  imageUrl?: string; // URL de imagen en Firebase Storage
  isActive: boolean;
  displayOrder?: number; // Order for display in admin panel and client-facing views
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// ========================================
// Transfer Types (Barber-to-Barber Client Transfers)
// ========================================

/**
 * Transfer status for barber-to-barber client transfers
 */
export enum TransferStatus {
  PENDING = 'pending',           // Transfer requested, awaiting response
  ACCEPTED = 'accepted',         // Transfer accepted by target barber
  REJECTED = 'rejected',         // Transfer rejected by target barber
  CANCELLED = 'cancelled',       // Transfer cancelled by requesting barber
  EXPIRED = 'expired',           // Transfer request expired (5 min timeout)
}

/**
 * Reasons for rejecting a client transfer
 */
export enum TransferRejectionReason {
  BUSY_WITH_CLIENTS = 'busy_with_clients',           // Ocupado con otros clientes
  OUT_OF_WORKING_HOURS = 'out_of_working_hours',     // Fuera de horario de trabajo
  OTHER_REASON = 'other_reason',                     // Otros motivos
}

/**
 * Human-readable labels for rejection reasons (i18n ready)
 */
export const TRANSFER_REJECTION_REASON_LABELS: Record<TransferRejectionReason, string> = {
  [TransferRejectionReason.BUSY_WITH_CLIENTS]: 'Ocupado con otros clientes',
  [TransferRejectionReason.OUT_OF_WORKING_HOURS]: 'Fuera de horario de trabajo',
  [TransferRejectionReason.OTHER_REASON]: 'Otros motivos',
};

/**
 * Transfer history entry for audit trail
 */
export interface TransferHistoryEntry {
  action: 'requested' | 'accepted' | 'rejected' | 'cancelled' | 'expired';
  fromBarberId: string;
  toBarberId: string;
  timestamp: Timestamp;
  reason?: TransferRejectionReason;
  notes?: string;
}

/**
 * Transfer information embedded in QueueTicket
 */
export interface QueueTransfer {
  // Barbers involved
  fromBarberId: string;
  fromBarberName: string;
  toBarberId: string;
  toBarberName: string;

  // Status
  status: TransferStatus;

  // Timestamps
  requestedAt: Timestamp;
  respondedAt?: Timestamp;
  expiresAt: Timestamp; // Auto-expire (see OVERTIME_REQUEST_TIMEOUT_MINUTES)

  // Rejection details (only if status === REJECTED)
  rejectionReason?: TransferRejectionReason;
  rejectionNotes?: string; // Max 200 characters

  // History for audit trail
  history: TransferHistoryEntry[];
}

// ========================================
// Queue Types
// ========================================

export type QueueStatus =
  | 'waiting'
  | 'notified'
  | 'arrived'
  | 'in_service'
  | 'finishing'
  | 'completed'
  | 'cancelled'
  | 'expired'
  | 'waiting_overtime_approval';

export interface QueueTicket {
  queueId: string;
  franchiseId: string;
  branchId: string;
  userId: string;
  position: number;
  ticketNumber: string;
  status: QueueStatus;
  serviceId?: string;
  barberId?: string;
  estimatedWaitTime: number; // minutos

  // Client info (denormalized for display)
  clientName?: string;
  clientEmail?: string;

  // Service info (denormalized for display)
  serviceName?: string;
  servicePrice?: number;
  barberName?: string;

  // Transfer data (optional)
  transfer?: QueueTransfer;

  // Original barber (before transfer)
  originalBarberId?: string;

  // Overtime request data (when booking outside barber's working hours)
  overtimeRequest?: OvertimeRequest;

  // Companion system - for clients accompanied by children/elderly
  isCompanion?: boolean; // True if this is a companion's ticket
  parentQueueId?: string; // Reference to the main client's ticket
  companionName?: string; // Name of the companion
  companionType?: 'child' | 'elder' | 'other'; // Type of companion
  companionQueueIds?: string[]; // Array of companion ticket IDs (on parent ticket)
  totalCompanions?: number; // Total number of companions (on parent ticket)

  // Loyalty/Stamp Card
  useStampCard?: boolean;           // Client is using stamp card for free service
  freeServiceValue?: number;        // Value of free service (e.g., 15€)
  amountToPay?: number;             // Amount to pay after discount (servicePrice - freeServiceValue)
  pointsEarned?: number;            // Points earned after completing this service

  // Reward Request (client requests to use reward voucher)
  rewardRequest?: {
    rewardId: string;
    rewardCode: string;
    requestedAt: Timestamp;
    status: 'pending' | 'approved' | 'rejected';
    serviceId: string;
    approvedBy?: string; // Barber ID who approved/rejected
    respondedAt?: Timestamp;
  };

  // Service timing
  serviceStartedAt?: Timestamp; // When barber started service (in_service status)
  serviceDuration?: number; // Duration of selected service in minutes
  finishingAt?: Timestamp; // When barber expects to finish (set by "Estoy acabando")

  timerExpiry?: Timestamp;
  arrivedAt?: Timestamp;
  calledAt?: Timestamp;
  completedAt?: Timestamp;
  cancelledAt?: Timestamp;
  expiredAt?: Timestamp;
  cancelReason?: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// ========================================
// Notification Types
// ========================================

export enum NotificationType {
  TICKET_CONFIRMED = 'ticket_confirmed',
  POSITION_UPDATE = 'position_update',
  YOUR_TURN = 'your_turn',
  ARRIVAL_REMINDER = 'arrival_reminder',
  TICKET_EXPIRED = 'ticket_expired',
  TICKET_CANCELLED = 'ticket_cancelled',
  QUEUE_PAUSED = 'queue_paused',
  QUEUE_RESUMED = 'queue_resumed',
}

export interface Notification {
  notificationId: string;
  userId: string;
  type: NotificationType;
  title: string;
  body: string;
  data?: Record<string, any>;
  isRead: boolean;
  isSent: boolean;
  sentAt?: Timestamp;
  readAt?: Timestamp;
  createdAt: Timestamp;
}

/**
 * Notification delivery channels
 */
export type NotificationChannel = 'push' | 'email' | 'sms' | 'in_app';

/**
 * Notification delivery status
 */
export type NotificationDeliveryStatus = 'pending' | 'sent' | 'delivered' | 'failed' | 'cancelled';

/**
 * Enhanced queue notification with delivery tracking and retry logic
 * Extends basic notification with multi-channel delivery and retry capabilities
 */
export interface QueueNotification {
  notificationId: string;
  queueId?: string; // Associated queue ticket
  userId: string;

  // Notification content
  type: NotificationType;
  title: string;
  message: string;
  data?: Record<string, any>;

  // Delivery configuration
  channels: NotificationChannel[]; // Channels to deliver through
  priority: 'low' | 'normal' | 'high' | 'urgent';

  // Delivery status
  deliveryStatus: NotificationDeliveryStatus;
  deliveryAttempts: number;
  maxRetries: number;
  lastAttemptAt?: Timestamp;
  sentAt?: Timestamp;
  deliveredAt?: Timestamp;
  failedAt?: Timestamp;

  // Delivery results per channel
  channelResults?: {
    [channel in NotificationChannel]?: {
      status: NotificationDeliveryStatus;
      sentAt?: Timestamp;
      error?: string;
    };
  };

  // Error tracking
  lastError?: string;

  // User interaction
  isRead: boolean;
  readAt?: Timestamp;

  // Scheduling
  scheduledFor?: Timestamp; // If notification should be sent at a specific time
  expiresAt?: Timestamp; // Notification expiry time

  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// Barber-specific notification types
export enum BarberNotificationType {
  NEW_QUEUE_TICKET = 'new_queue_ticket',
  CLIENT_ARRIVED = 'client_arrived',
  CLIENT_NO_SHOW = 'client_no_show',
  CLIENT_CANCELLED = 'client_cancelled',
  QUEUE_STATUS_CHANGE = 'queue_status_change',
  APPOINTMENT_REMINDER = 'appointment_reminder',
  SHIFT_REMINDER = 'shift_reminder',
  ADMIN_MESSAGE = 'admin_message',
  SYSTEM_ALERT = 'system_alert',
  // Transfer-related notification types
  TRANSFER_REQUESTED = 'transfer_requested',
  TRANSFER_ACCEPTED = 'transfer_accepted',
  TRANSFER_REJECTED = 'transfer_rejected',
  TRANSFER_CANCELLED = 'transfer_cancelled',
  TRANSFER_EXPIRED = 'transfer_expired',
  // Overtime request notification types
  OVERTIME_REQUESTED = 'overtime_requested',
  OVERTIME_ACCEPTED = 'overtime_accepted',
  OVERTIME_REJECTED = 'overtime_rejected',
  OVERTIME_EXPIRED = 'overtime_expired',
}

export type BarberNotificationPriority = 'low' | 'normal' | 'high' | 'urgent';

export interface BarberNotification {
  notificationId: string;
  barberId: string;
  franchiseId: string;
  branchId?: string;
  type: BarberNotificationType;
  priority: BarberNotificationPriority;
  title: string;
  body: string;
  data?: {
    queueId?: string;
    clientId?: string;
    clientName?: string;
    serviceName?: string;
    appointmentId?: string;
    // Transfer-specific data
    transferStatus?: TransferStatus;
    transferFromBarberId?: string;
    transferFromBarberName?: string;
    transferToBarberId?: string;
    transferToBarberName?: string;
    transferRejectionReason?: TransferRejectionReason;
    transferRejectionNotes?: string;
    [key: string]: any;
  };
  isRead: boolean;
  readAt?: Timestamp;
  expiresAt?: Timestamp;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// ========================================
// Loyalty Types
// ========================================

/**
 * Loyalty transaction types
 */
export type LoyaltyTransactionType =
  | 'earned'           // Points earned from completing service
  | 'redeemed'         // Points redeemed for rewards
  | 'penalty'          // Points deducted for no-show, late cancellation
  | 'bonus'            // Bonus points from promotions or special events
  | 'manual_adjustment' // Manual adjustment by admin
  | 'expired'          // Points expired due to inactivity
  | 'refund';          // Points refunded from cancelled service

/**
 * Loyalty points transaction record
 * Tracks all point movements with full audit trail
 */
export interface LoyaltyTransaction {
  transactionId: string;
  userId: string;
  franchiseId: string;
  branchId?: string;
  queueId?: string; // Associated queue ticket (if applicable)

  // Transaction details
  type: LoyaltyTransactionType;
  amount: number; // Positive for earning, negative for deduction

  // Balance tracking
  balanceBefore: number;
  balanceAfter: number;

  // Context
  reason: string; // Human-readable reason
  description?: string; // Additional details

  // Related entities
  serviceId?: string; // Service associated with transaction
  offerId?: string; // Promotion/offer ID (if bonus)
  barberId?: string; // Barber who processed (if applicable)

  // Audit trail
  processedBy?: string; // Admin user ID (for manual adjustments)
  metadata?: Record<string, any>; // Additional context data

  createdAt: Timestamp;
}

// ========================================
// Offer Types (Simplified - Display Only)
// ========================================

export type OfferBackgroundStyle =
  | 'purple'      // Purple to pink (default)
  | 'blue'        // Blue to cyan
  | 'green'       // Green to teal
  | 'orange'      // Orange to red
  | 'gold'        // Gold to amber
  | 'dark'        // Dark elegant
  | 'rainbow';    // Colorful gradient

export interface Offer {
  offerId: string;
  franchiseId: string;

  // Content
  title: string;           // Ej: "Black Friday"
  description: string;     // Ej: "Disfruta de un 20% en tu corte favorito"
  discountText: string;    // Ej: "20%" o "5€" - Texto libre para mostrar

  // Appearance
  backgroundStyle?: OfferBackgroundStyle;  // Estilo de fondo del modal

  // Image (optional - shown with gradient background if not provided)
  imageURL?: string;       // URL de imagen subida (logo/icono para la oferta)

  // Validity
  isActive: boolean;
  startDate: Timestamp;
  endDate: Timestamp;

  // Notifications tracking
  notificationsSent: boolean;  // Si ya se enviaron notificaciones
  notificationsSentAt?: Timestamp;

  // Metadata
  createdBy: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// ========================================
// Loyalty Card System Types (Fidelización con Sellos)
// ========================================

export type StampStatus = 'active' | 'expired' | 'used_in_reward';

export interface LoyaltyStamp {
  stampId: string;
  userId: string;
  franchiseId: string;
  branchId: string;

  // Metadata de obtención
  earnedAt: Timestamp;
  expiresAt: Timestamp | null;
  status: StampStatus;

  // Relación con servicio
  queueId: string;
  serviceId: string;
  barberId: string;

  // Auditoría
  createdBy: string; // User ID (barbero o sistema)
  createdMethod: 'automatic' | 'manual';
  adjustmentReason?: string; // Si fue manual

  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export type RewardStatus = 'generated' | 'active' | 'in_use' | 'redeemed' | 'expired' | 'cancelled';

export interface LoyaltyReward {
  rewardId: string;
  userId: string;
  franchiseId: string;
  code: string; // Código único para validar

  // Estado
  status: RewardStatus;

  // Generación
  generatedAt: Timestamp;
  generatedFromStamps: string[]; // IDs de sellos que lo generaron

  // Expiración
  expiresAt: Timestamp | null;
  expiredAt?: Timestamp;
  extensionCount?: number; // Veces que se extendió

  // Redención
  redeemedAt?: Timestamp;
  redeemedBy?: string; // Barbero ID
  redeemedAtBranch?: string;
  queueId?: string; // Ticket donde se usó

  // Metadata del premio
  rewardType: 'free_service';
  serviceId: string; // Servicio gratis (ej: 'haircut')
  value: number; // Valor monetario del premio en céntimos

  // Tipo de descuento: fijo en euros o porcentaje del servicio
  discountType?: 'fixed' | 'percentage'; // default 'fixed' for backwards compatibility
  discountPercentage?: number; // 1-100, only used when discountType === 'percentage'

  createdAt: Timestamp;
  updatedAt: Timestamp;
}

/**
 * Registro de pago de servicio
 * Se crea cuando un barbero completa un servicio y registra el método de pago
 */
export interface Payment {
  paymentId: string;

  // Referencias
  queueId: string; // ID del turno completado
  userId: string; // Cliente
  barberId: string; // Barbero que realizó el servicio (ID del documento en barbers)
  barberUserId?: string; // userId del barbero (para security rules)
  barberName?: string; // Nombre del barbero (para mostrar en UI)
  franchiseId: string;
  branchId: string;
  serviceId: string; // ID del servicio

  // Información del servicio
  serviceName: string;
  servicePrice: number; // Precio del servicio en céntimos
  serviceDuration?: number; // Duración en minutos

  // Método de pago
  paymentMethod: 'voucher' | 'card' | 'cash' | 'transfer' | 'bizum';

  // Si es voucher (vale)
  voucherId?: string; // ID del reward/vale usado
  voucherCode?: string; // Código del vale
  voucherDiscount?: number; // Descuento del vale en céntimos

  // Monto total y pagado
  totalAmount: number; // Monto total del servicio en céntimos
  paidAmount: number; // Monto que realmente pagó el cliente en céntimos

  // Commission tracking
  commissionRate?: number; // Commission percentage applied (0-100)
  commissionAmount?: number; // Commission amount in cents (servicePrice * commissionRate / 100)
  barberEarnings?: number; // Amount barber earns in cents

  // Metadata del cliente
  clientName: string;
  clientEmail?: string;

  // Timestamps
  completedAt: Timestamp; // Cuando se completó el servicio
  createdAt: Timestamp;

  // Notas opcionales
  notes?: string;
}

/**
 * Registro de canje de recompensa (DEPRECATED - usar Payment con paymentMethod: 'voucher')
 * Se mantiene por compatibilidad pero se recomienda usar Payment
 */
export interface RewardRedemption {
  redemptionId: string;
  rewardId: string;
  rewardCode: string;
  userId: string;
  barberId: string;
  franchiseId: string;
  branchId: string;
  serviceName: string;
  servicePrice: number;
  rewardDiscount: number;
  remainingAmount: number;
  paymentMethod?: 'cash' | 'card' | 'none';
  clientName: string;
  clientEmail?: string;
  usedAt: Timestamp;
  createdAt: Timestamp;
  notes?: string;
}

export interface LoyaltyConfig {
  franchiseId: string;

  enabled: boolean;
  pointsSystemEnabled?: boolean; // Master toggle to enable/disable entire points system
  stampsRequired: number;

  // Welcome configuration for new users
  welcomeMessage?: string; // Custom welcome message for new users
  welcomeStampEnabled?: boolean; // Give a gift stamp to new users
  welcomeStampCount?: number; // Number of gift stamps for new users (default: 1)

  // Points to Stamps Conversion
  pointsToStampsEnabled?: boolean; // Permite canjear puntos por sellos
  pointsPerStamp?: number; // Puntos necesarios para 1 sello (default: 100)

  stampExpiration: {
    enabled: boolean;
    days: number; // Días hasta expiración
  };

  rewardExpiration: {
    enabled: boolean;
    days: number; // Días hasta expiración
  };

  eligibleServices: {
    mode: 'all' | 'specific';
    serviceIds: string[]; // Si mode='specific', lista de IDs
  };

  stampsPerVisit?: {
    mode: 'one_per_ticket' | 'one_per_service';
    maxPerTicket: number; // Máximo sellos por ticket
  };

  autoApplyReward?: boolean; // ¿Aplicar premio automáticamente?

  notifications: {
    onStampEarned: boolean;
    onRewardGenerated: boolean;
    onRewardExpiring: boolean;
    onStampExpiring?: boolean;
    expiringDaysBefore?: number; // Días antes para notificar
  };

  crossBranchRedemption?: boolean; // ¿Canjear en cualquier sucursal?
  allowStampOnFreeService?: boolean; // ¿Dar sello en servicios gratuitos?
  preventSelfStamping?: boolean; // ¿Prevenir auto-sellos? (siempre true)

  // Auditoría
  updatedAt?: Timestamp;
  updatedBy?: string; // User ID del admin que actualizó
  createdAt?: Timestamp;
}

export interface FranchiseLoyaltySummary {
  stamps: number;
  required: number;
  percentage: number;
}

export interface LoyaltyCustomerSummary {
  userId: string;

  // Por franquicia
  franchises: {
    [franchiseId: string]: {
      activeStamps: number;
      totalStampsEarned: number;
      totalRewardsGenerated: number;
      totalRewardsRedeemed: number;
      totalRewardsExpired: number;

      currentProgress: FranchiseLoyaltySummary;

      activeRewards: string[]; // IDs de premios activos

      lastStampAt?: Timestamp;
      lastRewardAt?: Timestamp;
    };
  };

  // Global
  totalStampsEarned: number;
  totalRewardsRedeemed: number;

  updatedAt: Timestamp;
  createdAt: Timestamp;
}

// Extensión del QueueTicket para incluir información de redención
export interface QueueTicketWithReward extends QueueTicket {
  loyaltyReward?: {
    rewardId: string;
    code: string;
    appliedAt: Timestamp;
    appliedBy: string; // Barbero ID
    discountAmount: number; // 100% del servicio si es gratis
    originalPrice: number;
    finalPrice: number; // 0 si gratis
  };

  pricingBreakdown?: {
    subtotal: number;
    loyaltyDiscount: number; // Descuento por premio
    otherDiscounts: number;
    taxes: number;
    total: number;
  };
}

// ========================================
// Posts Types (Instagram-style feed)
// ========================================

export interface Post {
  postId: string;
  franchiseId: string; // CRITICAL: Required for multi-franchise filtering
  authorId: string; // barberId o adminId
  authorName: string;
  authorPhoto?: string;
  authorRole: UserRole;
  branchId?: string;
  branchName?: string;
  imageUrl: string; // Foto del corte
  caption?: string; // Descripción
  tags?: string[]; // Tags: #fade, #undercut, etc
  taggedUsers?: PostTag[]; // Users tagged in this photo
  likesCount: number;
  commentsCount: number;
  isActive: boolean;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface PostLike {
  likeId: string;
  postId: string;
  userId: string;
  userName: string;
  userPhoto?: string;
  createdAt: Timestamp;
}

export interface PostComment {
  commentId: string;
  postId: string;
  userId: string;
  userName: string;
  userPhoto?: string;
  userRole: UserRole;
  text: string;
  createdAt: Timestamp;
  updatedAt?: Timestamp;
}

// Post Permissions (Phase 1)
export interface PostPermission {
  permissionId: string;
  userId: string;
  franchiseId: string;

  // Capabilities
  canCreate: boolean;      // Can create posts
  canEdit: boolean;        // Can edit their own posts
  canDelete: boolean;      // Can delete their own posts
  canEditAny: boolean;     // Can edit any post (admin/super_admin)
  canDeleteAny: boolean;   // Can delete any post (admin/super_admin)

  // Metadata
  grantedBy: string;       // userId who granted permission
  grantedAt: Timestamp;
  revokedAt?: Timestamp;
  isActive: boolean;

  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// Post Notifications (Phase 2)
export type PostNotificationType =
  | 'post_like'        // Someone liked my post
  | 'post_comment'     // Someone commented on my post
  | 'comment_reply'    // Someone replied to my comment
  | 'post_mention'     // Someone tagged me in a photo
  | 'post_created';    // Followed barber posted (future)

export interface PostNotification {
  notificationId: string;
  type: PostNotificationType;

  // Target
  userId: string;          // Who receives notification
  franchiseId: string;

  // Source
  triggeredBy: string;     // Who triggered it
  triggeredByName: string;
  triggeredByPhoto?: string;

  // Related entities
  postId: string;
  commentId?: string;      // If type = comment/reply

  // Content
  title: string;
  body: string;
  imageUrl?: string;       // Post thumbnail

  // Metadata
  isRead: boolean;
  readAt?: Timestamp;
  createdAt: Timestamp;
  expiresAt?: Timestamp;   // Auto-delete after 30 days
}

// Post Tagging (Phase 4)
export interface PostTag {
  userId: string;
  userName: string;
  userPhoto?: string;
  userRole: UserRole;
  taggedBy: string;        // Who added the tag
  taggedAt: Timestamp;
}

// Post Notification Preferences (Phase 3)
export interface PostNotificationPreferences {
  enabled: boolean;              // Master switch

  // What to notify
  notifyOnPostLikes: boolean;    // When someone likes my post
  notifyOnPostComments: boolean; // When someone comments my post
  notifyOnCommentReplies: boolean; // When someone replies to my comment
  notifyOnMentions: boolean;     // When tagged in a photo
  notifyOnNewPosts: boolean;     // When followed barbers post (future)

  // Filter
  onlyMyContent: boolean;        // Only notify about my posts/comments

  // Channels
  pushNotifications: boolean;    // In-app + push
  emailNotifications: boolean;   // Email digest (future)
}

// Appointments
export interface Appointment {
  appointmentId: string;
  franchiseId: string;
  branchId: string;
  userId: string;
  userName: string;
  userEmail: string;
  userPhone?: string;
  barberId: string;
  barberName: string;
  serviceId: string;
  serviceName: string;
  servicePrice: number;
  serviceDuration: number;
  dateTime: Timestamp;
  status: 'pending' | 'confirmed' | 'in_progress' | 'completed' | 'cancelled' | 'no_show';
  notes?: string;
  reminderSent?: boolean;
  queueId?: string; // Set when appointment is converted to queue ticket
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

/**
 * VIP appointment status types
 */
export type VIPAppointmentStatus =
  | 'requested'      // Initial request by VIP user
  | 'accepted'       // Accepted by barber/admin
  | 'rejected'       // Rejected by barber/admin
  | 'confirmed'      // Confirmed and scheduled
  | 'in_progress'    // Service in progress
  | 'completed'      // Service completed
  | 'cancelled'      // Cancelled by user or admin
  | 'no_show';       // User did not show up

/**
 * VIP Appointment with priority handling
 * Extends regular appointments with VIP-specific features like requests and priority
 */
export interface VIPAppointment {
  appointmentId: string;
  franchiseId: string;
  branchId: string;

  // User information
  userId: string;
  userName: string;
  userEmail: string;
  userPhone?: string;
  vipLevel?: number; // VIP tier level (1-5, higher = more priority)

  // Scheduling
  requestedDateTime: Timestamp; // User's preferred time
  scheduledDateTime?: Timestamp; // Actual scheduled time (may differ from requested)

  // Service details
  preferredBarberId?: string; // User's preferred barber
  preferredBarberName?: string;
  assignedBarberId?: string; // Actually assigned barber
  assignedBarberName?: string;

  serviceId: string;
  serviceName: string;
  servicePrice: number;
  serviceDuration: number;

  // Status and priority
  status: VIPAppointmentStatus;
  priority: number; // Higher number = higher priority (calculated from VIP level, history, etc.)

  // Request/Response workflow
  requestNotes?: string; // Notes from VIP user when requesting
  rejectionReason?: string; // Reason if rejected
  rejectionNotes?: string; // Additional notes for rejection

  acceptedBy?: string; // Admin/barber user ID who accepted
  acceptedAt?: Timestamp;
  rejectedBy?: string; // Admin/barber user ID who rejected
  rejectedAt?: Timestamp;

  // Notifications
  reminderSent?: boolean;
  confirmationSent?: boolean;

  // Additional notes
  internalNotes?: string; // Admin/barber notes (not visible to client)
  clientNotes?: string; // Client's notes about preferences

  // Timestamps
  createdAt: Timestamp;
  updatedAt: Timestamp;
  completedAt?: Timestamp;
  cancelledAt?: Timestamp;
}

// ========================================
// Queue Configuration Types
// ========================================

/**
 * System-wide queue configuration
 * All configurable parameters for the queue system
 * Can be adjusted by super_admin to tune system behavior
 */
export interface QueueConfig {
  configId: string;
  franchiseId: string;
  branchId?: string; // If not set, applies to all branches in franchise

  // Timeout configurations (all in minutes)
  timeouts: {
    arrivalWindow: number; // Time window for client to arrive after being notified (default: 10 min)
    courtesyTime: number; // Grace period before marking as no-show (default: 5 min)
    expirationTime: number; // Time before ticket auto-expires if no activity (default: 120 min)
    notificationAdvance: number; // How far ahead to notify clients (default: 15 min)
  };

  // Penalty configuration
  penalties: {
    noShow: number; // Points deducted for no-show (default: -10)
    lateArrival: number; // Points deducted for late arrival (default: -5)
    lateCancellation: number; // Points deducted for cancelling within grace period (default: -3)
  };

  // Loyalty configuration
  loyalty: {
    pointsPerService: number; // Base points earned per completed service (default: 10)
    vipThreshold: number; // Points needed to become VIP (default: 100)
    bonusMultiplier: number; // Multiplier for bonus points during promotions (default: 1.5)
  };

  // Queue behavior
  queueBehavior: {
    maxQueueSize: number; // Maximum queue size per branch (default: 50)
    enableVIPPriority: boolean; // Allow VIP users to skip ahead (default: true)
    enableBarberSelection: boolean; // Allow clients to choose barber (default: true)
    autoAssignBarber: boolean; // Auto-assign available barber if not selected (default: true)
  };

  // Notification settings
  notifications: {
    enablePushNotifications: boolean; // Enable push notifications (default: true)
    enableEmailNotifications: boolean; // Enable email notifications (default: true)
    enableSMSNotifications: boolean; // Enable SMS notifications (default: false)
    positionUpdateThreshold: number; // Notify every N position changes (default: 3)
  };

  // Business rules
  businessRules: {
    minServiceDuration: number; // Minimum service duration in minutes (default: 15)
    maxServiceDuration: number; // Maximum service duration in minutes (default: 180)
    allowAnonymousQueue: boolean; // Allow guest users to join queue (default: true)
    requireServiceSelection: boolean; // Require service selection when joining (default: false)
  };

  // Default values documentation
  defaults?: {
    description: string;
    recommendedValues: Record<string, any>;
  };

  // Validation constraints
  constraints?: {
    minArrivalWindow: number;
    maxArrivalWindow: number;
    minCourtesyTime: number;
    maxCourtesyTime: number;
  };

  // Audit
  updatedBy: string; // User ID of super_admin who last updated
  updatedAt: Timestamp;
  createdAt: Timestamp;
}

// ========================================
// Landing Config Types
// ========================================

export interface LandingFeature {
  icon: string;
  titleKey: string; // Translation key for title
  descriptionKey: string; // Translation key for description
  order: number; // Display order
}

export interface LandingConfig {
  // Business hours
  schedule: {
    weekdays: string; // e.g., "10:00 - 21:00"
    saturday: string; // e.g., "10:00 - 20:00"
    sunday: string;   // e.g., "Cerrado"
  };

  // Average wait time displayed on hero section
  averageWaitTime: string; // e.g., "15min"

  // Feature cards displayed at bottom of home page
  features: LandingFeature[];

  // Metadata
  updatedAt: Timestamp;
}

// ========================================
// Loyalty/Rewards System Types
// ========================================

/**
 * Points calculation method for services
 */
export type PointsCalculationMethod = 'fixed' | 'percentage';

/**
 * Loyalty/Rewards configuration per franchise
 * All values are configurable from admin panel
 */
// DEPRECATED: Old LoyaltyConfig interface - kept for reference
// export interface LoyaltyConfigOld {
//   configId: string;
//   franchiseId: string;
//
//   // Stamp Card Configuration
//   stampCard: {
//     enabled: boolean;
//     stampsRequired: number;        // Number of stamps needed for free service (default: 10)
//     freeServiceValue: number;      // Value of free service in € (default: 15)
//     autoAddStamp: boolean;         // Auto-add stamp on service completion (default: true)
//   };
//
//   // Points System Configuration
//   points: {
//     enabled: boolean;
//     calculationMethod: PointsCalculationMethod;  // 'fixed' or 'percentage'
//     fixedPointsPerService: number;              // Fixed points per service (if method = 'fixed')
//     pointsPerEuro: number;                      // Points per € spent (if method = 'percentage')
//     showInNotifications: boolean;               // Show points earned in notifications
//   };
//
//   // Points Exchange Configuration
//   exchange: {
//     pointsPerStamp: number;        // Points needed to get 1 stamp (default: 100)
//     pointsForVIP: number;          // Points needed to become VIP (default: 500)
//     vipDurationDays: number;       // VIP duration in days (default: 30)
//   };
//
//   // VIP Benefits Configuration
//   vipBenefits: {
//     priorityQueue: boolean;        // VIP gets higher priority in queue
//     discountPercentage: number;    // % discount on services (default: 10)
//     freeServices: boolean;         // Access to exclusive services
//   };
//
//   // Metadata
//   createdAt: Timestamp;
//   updatedAt: Timestamp;
// }

// ========================================
// Utility Types and Type Guards
// ========================================

/**
 * Partial update types for common entities
 */
export type QueueTicketUpdate = Partial<Omit<QueueTicket, 'queueId' | 'createdAt'>>;
export type BarberStatusUpdate = Partial<Omit<BarberStatus, 'barberId' | 'createdAt'>>;
export type ServiceUpdate = Partial<Omit<Service, 'serviceId' | 'franchiseId' | 'createdAt'>>;
export type QueueConfigUpdate = Partial<Omit<QueueConfig, 'configId' | 'franchiseId' | 'createdAt'>>;

/**
 * Type guard for QueueTicket
 */
export function isQueueTicket(obj: any): obj is QueueTicket {
  return (
    obj &&
    typeof obj === 'object' &&
    typeof obj.queueId === 'string' &&
    typeof obj.franchiseId === 'string' &&
    typeof obj.branchId === 'string' &&
    typeof obj.userId === 'string' &&
    typeof obj.position === 'number' &&
    typeof obj.ticketNumber === 'string' &&
    isValidQueueStatus(obj.status)
  );
}

/**
 * Type guard for valid QueueStatus
 */
export function isValidQueueStatus(status: any): status is QueueStatus {
  return (
    status === 'waiting' ||
    status === 'notified' ||
    status === 'arrived' ||
    status === 'in_service' ||
    status === 'completed' ||
    status === 'cancelled' ||
    status === 'expired'
  );
}

/**
 * Type guard for valid BarberStatusType
 */
export function isValidBarberStatus(status: any): status is BarberStatusType {
  return (
    status === 'available' ||
    status === 'busy' ||
    status === 'on_break' ||
    status === 'offline'
  );
}

/**
 * Type guard for valid NotificationType
 */
export function isValidNotificationType(type: any): type is NotificationType {
  return Object.values(NotificationType).includes(type);
}

/**
 * Type guard for valid LoyaltyTransactionType
 */
export function isValidLoyaltyTransactionType(type: any): type is LoyaltyTransactionType {
  return (
    type === 'earned' ||
    type === 'redeemed' ||
    type === 'penalty' ||
    type === 'bonus' ||
    type === 'manual_adjustment' ||
    type === 'expired' ||
    type === 'refund'
  );
}

/**
 * Type guard for VIPAppointmentStatus
 */
export function isValidVIPAppointmentStatus(status: any): status is VIPAppointmentStatus {
  return (
    status === 'requested' ||
    status === 'accepted' ||
    status === 'rejected' ||
    status === 'confirmed' ||
    status === 'in_progress' ||
    status === 'completed' ||
    status === 'cancelled' ||
    status === 'no_show'
  );
}

/**
 * Type guard for ServiceCategory
 */
export function isValidServiceCategory(category: any): category is ServiceCategory {
  return (
    category === 'haircut' ||
    category === 'beard' ||
    category === 'combo' ||
    category === 'color' ||
    category === 'styling' ||
    category === 'treatment' ||
    category === 'other'
  );
}

/**
 * Type guard for NotificationChannel
 */
export function isValidNotificationChannel(channel: any): channel is NotificationChannel {
  return (
    channel === 'push' ||
    channel === 'email' ||
    channel === 'sms' ||
    channel === 'in_app'
  );
}

/**
 * Helper to check if a ticket is active (not in terminal state)
 */
export function isActiveTicket(ticket: QueueTicket): boolean {
  return (
    ticket.status === 'waiting' ||
    ticket.status === 'notified' ||
    ticket.status === 'arrived' ||
    ticket.status === 'in_service'
  );
}

/**
 * Helper to check if a ticket is in terminal state
 */
export function isTerminalTicket(ticket: QueueTicket): boolean {
  return (
    ticket.status === 'completed' ||
    ticket.status === 'cancelled' ||
    ticket.status === 'expired'
  );
}

/**
 * Helper to check if user is VIP
 */
export function isVIPUser(user: User): boolean {
  return user.isVIP === true;
}

/**
 * Helper to check if user has sufficient loyalty points
 */
export function hasMinimumPoints(user: User, minimumPoints: number): boolean {
  return user.queuePoints >= minimumPoints;
}

// ========================================
// Overtime Request Types (Solicitud fuera de horario)
// ========================================

/**
 * Status for overtime/out-of-schedule ticket requests
 */
export enum OvertimeRequestStatus {
  PENDING = 'pending',           // Request sent, waiting for barber response
  ACCEPTED = 'accepted',         // Barber accepted to work overtime
  REJECTED = 'rejected',         // Barber rejected the request
  EXPIRED = 'expired',           // Request expired (5 min timeout)
  CANCELLED = 'cancelled',       // Client cancelled the request
}

/**
 * Reasons for rejecting an overtime request
 */
export enum OvertimeRejectionReason {
  PERSONAL_COMMITMENT = 'personal_commitment',     // Compromiso personal
  TOO_TIRED = 'too_tired',                         // Muy cansado
  NEXT_DAY_EARLY = 'next_day_early',               // Turno temprano mañana
  OTHER_REASON = 'other_reason',                   // Otros motivos
}

/**
 * Human-readable labels for overtime rejection reasons (i18n ready)
 */
export const OVERTIME_REJECTION_REASON_LABELS: Record<OvertimeRejectionReason, string> = {
  [OvertimeRejectionReason.PERSONAL_COMMITMENT]: 'Compromiso personal',
  [OvertimeRejectionReason.TOO_TIRED]: 'Muy cansado',
  [OvertimeRejectionReason.NEXT_DAY_EARLY]: 'Turno temprano mañana',
  [OvertimeRejectionReason.OTHER_REASON]: 'Otros motivos',
};

/**
 * Overtime request history entry for audit trail
 */
export interface OvertimeHistoryEntry {
  action: 'requested' | 'accepted' | 'rejected' | 'cancelled' | 'expired';
  userId: string;
  barberId: string;
  timestamp: Timestamp;
  reason?: OvertimeRejectionReason;
  notes?: string;
}

/**
 * Overtime request information embedded in QueueTicket
 * When a client requests a ticket outside the barber's working hours
 */
export interface OvertimeRequest {
  // Request details
  barberId: string;
  barberName: string;
  userId: string;
  userName: string;

  // Schedule context
  barberWorkEnd: string; // e.g., "20:00" - when barber's shift ends
  estimatedServiceEnd: string; // e.g., "20:30" - when service would end
  overtimeMinutes: number; // How many minutes past shift end

  // Service info
  serviceId: string;
  serviceName: string;
  serviceDuration: number;
  branchId: string;

  // Status
  status: OvertimeRequestStatus;

  // Timestamps
  requestedAt: Timestamp;
  respondedAt?: Timestamp;
  expiresAt: Timestamp; // Auto-expire (see OVERTIME_REQUEST_TIMEOUT_MINUTES)

  // Rejection details (only if status === REJECTED)
  rejectionReason?: OvertimeRejectionReason;
  rejectionNotes?: string; // Max 200 characters

  // History for audit trail
  history: OvertimeHistoryEntry[];
}

/**
 * Type guard for valid OvertimeRequestStatus
 */
export function isValidOvertimeRequestStatus(status: any): status is OvertimeRequestStatus {
  return Object.values(OvertimeRequestStatus).includes(status);
}

/**
 * Type guard for valid OvertimeRejectionReason
 */
export function isValidOvertimeRejectionReason(reason: any): reason is OvertimeRejectionReason {
  return Object.values(OvertimeRejectionReason).includes(reason);
}

/**
 * Check if an overtime request is still pending and not expired
 */
export function isOvertimeRequestActive(request: OvertimeRequest): boolean {
  if (request.status !== OvertimeRequestStatus.PENDING) {
    return false;
  }
  // Check if expired (expiresAt is in the past)
  const now = new Date().getTime();
  const expiresAtMs = request.expiresAt instanceof Date
    ? request.expiresAt.getTime()
    : (request.expiresAt as any).toMillis?.() || now;
  return expiresAtMs > now;
}

// ========================================
// Job Applications
// ========================================

export type JobApplicationStatus = 'pending' | 'reviewed' | 'contacted' | 'hired' | 'rejected';

export interface JobApplication {
  applicationId: string;
  franchiseId: string;

  // Applicant info
  fullName: string;
  email: string;
  phone: string;

  // Optional CV
  cvUrl?: string;
  cvFileName?: string;

  // Optional message
  message?: string;

  // Status
  status: JobApplicationStatus;
  notes?: string; // Admin notes

  // Metadata
  createdAt: Timestamp;
  updatedAt: Timestamp;
  reviewedBy?: string;
  reviewedAt?: Timestamp;
}

// ========================================
// Banner Config Types (TV Queue Display)
// ========================================

export type BannerIconName = 'star' | 'bulb' | 'phone' | 'heart' | 'scissors' | 'gift';

export interface BannerSlide {
  id: string;
  title: string;
  text: string;
  icon?: BannerIconName;
  imageUrl?: string;
  isActive: boolean;
  order: number;
}

export interface BannerConfig {
  franchiseId: string;
  enabled: boolean;
  transitionInterval: number; // seconds (default 8)
  slides: BannerSlide[];
  updatedAt?: Timestamp;
  updatedBy?: string;
}

// ========================================
// Franchise Applications
// ========================================

export type FranchiseApplicationStatus = 'pending' | 'reviewing' | 'meeting_scheduled' | 'approved' | 'rejected';

export interface FranchiseApplication {
  applicationId: string;

  // Applicant info
  fullName: string;
  email: string;
  phone: string;
  city: string; // Ciudad donde quiere abrir la franquicia

  // Optional message
  message?: string;

  // Status
  status: FranchiseApplicationStatus;
  notes?: string; // Admin notes

  // Metadata
  createdAt: Timestamp;
  updatedAt: Timestamp;
  reviewedBy?: string;
  reviewedAt?: Timestamp;
}
